import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, apiFetchDownload } from "../../api/apiFetch";
import ConfirmModal from "../../components/ConfirmModal";
import ErrorModal from "../../components/ErrorModal";
import { usePermission } from "../../hooks/usePermission";
import TimeInput from "./TimeInput";
import InternalMonth from "./InternalMonth";
import {
  ABSENCE_LABELS,
  MONTH_NAMES,
  PERIOD_STATUS,
  apiErrorMessage,
  defaultBreak,
  formatDate,
  formatEuro,
  formatHoursMinutes,
  groupEntriesByDescription,
  sumEntries,
  todayIso,
  trimSeconds,
  weekdayName,
  workedMinutes,
} from "../../utils/timesheets";

const inputClass =
  "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

const KINDS = [
  { key: "work", label: "Prestatie" },
  { key: "absent", label: "Afwezig" },
  { key: "sick", label: "Ziek" },
];

function monthOf(iso) {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) };
}

function shiftMonth({ year, month }, delta) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

const SHORT_MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

/** Klik op de maandnaam: jaar- en maandrooster om meteen naar een andere maand te springen. */
function MonthPicker({ view, label, onPick }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(view.year);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const toggle = () => {
    setYear(view.year);
    setOpen((cur) => !cur);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Naar een andere maand"
        className="min-w-[9rem] px-2 py-1.5 rounded text-center font-semibold text-gray-800 capitalize hover:bg-gray-50"
      >
        {label}
      </button>
      {open && (
        <div role="dialog" aria-label="Maand kiezen" className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-64 bg-white rounded-lg border border-gray-200 shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setYear(year - 1)} aria-label="Vorig jaar" className="px-2 py-1 rounded text-gray-600 hover:bg-gray-100">‹</button>
            <span className="text-sm font-semibold text-gray-800">{year}</span>
            <button type="button" onClick={() => setYear(year + 1)} aria-label="Volgend jaar" className="px-2 py-1 rounded text-gray-600 hover:bg-gray-100">›</button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MONTH_NAMES.map((name, index) => {
              const current = year === view.year && index + 1 === view.month;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onPick({ year, month: index + 1 }); setOpen(false); }}
                  aria-current={current ? "true" : undefined}
                  className={`px-2 py-1.5 rounded text-sm capitalize ${current ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`}
                >
                  {SHORT_MONTHS[index]}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function emptyForm(date) {
  return { kind: "work", date, lineId: "", start: "", end: "", breakMinutes: "", breakTouched: false, quantity: "", note: "" };
}

function withEntries(data, entries) {
  return { ...data, entries, totals: sumEntries(entries) };
}

/** Tijdelijke rij voor de directe weergave: uren en kost lokaal berekend met dezelfde regels als de server. */
function provisionalEntry(form, line, tempId) {
  const base = {
    id: tempId, pending: true, entry_date: form.date, note: form.note || null, minutes: 0, cost: null,
    line_description: null, compensation_type: null, rate: null, start_time: null, end_time: null,
    break_minutes: null, quantity: null, contract_line_id: null, absence_type: null,
  };
  if (form.kind !== "work") return { ...base, entry_type: "absence", absence_type: form.kind };
  const entry = {
    ...base, entry_type: "work", contract_line_id: line.id, line_description: line.description,
    compensation_type: line.compensation_type, rate: line.rate,
  };
  if (line.compensation_type === "hourly") {
    const minutes = workedMinutes(form.start, form.end, form.breakMinutes) || 0;
    return {
      ...entry, minutes, start_time: form.start, end_time: form.end,
      break_minutes: form.breakMinutes !== "" ? Number(form.breakMinutes) : null,
      cost: Math.round(Number(line.rate) * minutes / 60 * 100) / 100,
    };
  }
  const quantity = Number(form.quantity);
  return { ...entry, quantity, cost: Math.round(Number(line.rate) * quantity * 100) / 100 };
}

function formFromEntry(entry) {
  return {
    kind: entry.entry_type === "absence" ? entry.absence_type : "work",
    date: entry.entry_date,
    lineId: entry.contract_line_id || "",
    start: trimSeconds(entry.start_time),
    end: trimSeconds(entry.end_time),
    breakMinutes: entry.break_minutes === null || entry.break_minutes === undefined ? "" : String(entry.break_minutes),
    breakTouched: true,
    quantity: entry.quantity === null || entry.quantity === undefined ? "" : String(entry.quantity),
    note: entry.note || "",
  };
}

/** Tab "Prestaties ingeven": maandnavigatie, ingaveblok en de maandtabel gegroepeerd per omschrijving. */
export default function TimesheetEntries({ memberId, ownId, isInternal, staff = [], initialView }) {
  const { hasAnyPermission } = usePermission();
  const canApprove = hasAnyPermission("timesheets.approve");
  const canRelease = hasAnyPermission("timesheets.release");
  const canReopen = hasAnyPermission("timesheets.settings");
  const canPdf = hasAnyPermission("timesheets.download_pdf");

  const [view, setView] = useState(initialView || monthOf(todayIso()));
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState(() => {
    const today = todayIso();
    if (!initialView || (monthOf(today).year === initialView.year && monthOf(today).month === initialView.month)) {
      return emptyForm(today);
    }
    return emptyForm(`${initialView.year}-${String(initialView.month).padStart(2, "0")}-01`);
  });
  const [editing, setEditing] = useState(null); // { id, original }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorModal, setErrorModal] = useState("");
  const [confirm, setConfirm] = useState(null); // { type, entry? }
  const [releaseReason, setReleaseReason] = useState("");
  const tempCounter = useRef(0);

  const status = data?.period?.status || "open";
  const loaded = data !== null;
  const editable = loaded && status === "open";
  const line = useMemo(() => lines.find((l) => l.id === form.lineId) || null, [lines, form.lineId]);
  const memberQuery = memberId ? `&member_id=${memberId}` : "";

  const loadMonth = useCallback(async () => {
    if (!memberId) return;
    try {
      setData(await apiFetch(`/timesheets/entries?year=${view.year}&month=${view.month}${memberQuery}`));
    } catch (err) {
      setErrorModal(apiErrorMessage(err));
    }
  }, [memberId, view, memberQuery]);

  useEffect(() => {
    setData(null);
    loadMonth();
  }, [loadMonth]);

  // lijnen die op de gekozen datum lopen
  useEffect(() => {
    if (!memberId || !form.date) return;
    (async () => {
      try {
        const result = await apiFetch(`/timesheets/entry-lines?date=${form.date}${memberQuery}`);
        setLines(result);
        setForm((cur) => {
          if (result.some((l) => l.id === cur.lineId)) return cur;
          return { ...cur, lineId: result.length === 1 ? result[0].id : "" };
        });
      } catch (err) {
        setLines([]);
      }
    })();
  }, [memberId, form.date, memberQuery]);

  // pauze voorinvullen volgens de lijn, zolang de gebruiker ze niet zelf aangepast heeft
  useEffect(() => {
    if (form.kind !== "work" || form.breakTouched) return;
    const suggestion = defaultBreak(line, form.start, form.end);
    setForm((cur) => (cur.breakMinutes === suggestion ? cur : { ...cur, breakMinutes: suggestion }));
  }, [line, form.start, form.end, form.kind, form.breakTouched]);

  const update = (patch) => setForm((cur) => ({ ...cur, ...patch }));

  const changeDate = (date) => {
    update({ date });
    if (date) setView(monthOf(date));
  };

  const goToMonth = (delta) => showMonth(shiftMonth(view, delta));

  const showMonth = (next) => {
    setView(next);
    setEditing(null);
    const first = `${next.year}-${String(next.month).padStart(2, "0")}-01`;
    const today = todayIso();
    setForm((cur) => ({ ...cur, date: monthOf(today).year === next.year && monthOf(today).month === next.month ? today : first }));
  };

  const isHourly = line?.compensation_type === "hourly";
  const isPerItem = line?.compensation_type === "per_item";
  const minutes = form.kind === "work" && isHourly ? workedMinutes(form.start, form.end, form.breakMinutes) : null;
  const liveCost = useMemo(() => {
    if (form.kind !== "work" || !line) return null;
    if (isHourly && minutes) return (Number(line.rate) * minutes) / 60;
    if (isPerItem && Number(form.quantity) > 0) return Number(line.rate) * Number(form.quantity);
    return null;
  }, [form.kind, line, isHourly, isPerItem, minutes, form.quantity]);

  const clearForm = () => {
    setEditing(null);
    setError("");
    setForm((cur) => ({ ...emptyForm(cur.date), lineId: cur.lineId }));
  };

  const buildPayload = (f, l) => {
    if (f.kind !== "work") {
      return { entry_type: "absence", entry_date: f.date, absence_type: f.kind, note: f.note || null };
    }
    const hourly = l.compensation_type === "hourly";
    return {
      entry_type: "work",
      entry_date: f.date,
      contract_line_id: f.lineId,
      start_time: hourly ? f.start : null,
      end_time: hourly ? f.end : null,
      break_minutes: hourly && f.breakMinutes !== "" ? Number(f.breakMinutes) : null,
      quantity: hourly ? null : Number(f.quantity),
      note: f.note || null,
    };
  };

  const validate = () => {
    if (!form.date) return "Kies een datum.";
    if (form.kind !== "work") return "";
    if (!form.lineId) return "Kies een contractlijn.";
    if (isHourly) {
      if (!form.start || !form.end) return "Geef start- en einduur in.";
      if (minutes === null) return "Het einduur moet na het startuur liggen en de pauze moet korter zijn dan de prestatie.";
    }
    if (isPerItem && !(Number(form.quantity) > 0)) return "Geef een aantal in.";
    return "";
  };

  // De rij verschijnt meteen; het serverantwoord vervangt ze, bij een fout verdwijnt ze en komt het formulier terug.
  const submit = (e) => {
    e.preventDefault();
    if (!data) return setError("Even geduld, de maand wordt nog geladen.");
    const problem = validate();
    if (problem) return setError(problem);
    setError("");

    const sentForm = form;
    const sentLine = line;
    const payload = buildPayload(sentForm, sentLine || {});
    const targetMonth = monthOf(sentForm.date);
    const inView = targetMonth.year === view.year && targetMonth.month === view.month;

    if (editing) {
      const { id, original } = editing;
      const optimistic = { ...provisionalEntry(sentForm, sentLine, id), id };
      if (inView) setData((cur) => withEntries(cur, cur.entries.map((en) => (en.id === id ? optimistic : en))));
      clearForm();
      const patch = { ...payload };
      delete patch.entry_type;
      apiFetch(`/timesheets/entries/${id}`, { method: "PATCH", body: JSON.stringify(patch) })
        .then((saved) => {
          if (inView) setData((cur) => withEntries(cur, cur.entries.map((en) => (en.id === id ? { ...en, ...saved, pending: false } : en))));
          else loadMonth();
        })
        .catch((err) => {
          setData((cur) => withEntries(cur, cur.entries.map((en) => (en.id === id ? original : en))));
          setEditing({ id, original });
          setForm(sentForm);
          setError(apiErrorMessage(err));
        });
      return;
    }

    tempCounter.current += 1;
    const tempId = `tmp-${tempCounter.current}`;
    if (inView) setData((cur) => withEntries(cur, [...cur.entries, provisionalEntry(sentForm, sentLine, tempId)]));
    clearForm();
    apiFetch("/timesheets/entries", { method: "POST", body: JSON.stringify({ ...payload, ...(memberId ? { member_id: memberId } : {}) }) })
      .then((saved) => {
        if (inView) setData((cur) => withEntries(cur, cur.entries.map((en) => (en.id === tempId ? { ...en, ...saved, pending: false } : en))));
      })
      .catch((err) => {
        setData((cur) => (cur ? withEntries(cur, cur.entries.filter((en) => en.id !== tempId)) : cur));
        setForm(sentForm);
        setError(apiErrorMessage(err));
      });
  };

  const startEdit = (entry) => {
    setEditing({ id: entry.id, original: entry });
    setError("");
    setForm(formFromEntry(entry));
  };

  const runAction = async (path, extra = {}) => {
    setSaving(true);
    try {
      await apiFetch(`/timesheets/periods/${path}`, {
        method: "POST",
        body: JSON.stringify({ year: view.year, month: view.month, member_id: memberId || null, ...extra }),
      });
      setConfirm(null);
      setReleaseReason("");
      await loadMonth();
    } catch (err) {
      setConfirm(null);
      setErrorModal(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      await apiFetchDownload(`/timesheets/periods/pdf?year=${view.year}&month=${view.month}&member_id=${memberId}`);
    } catch (err) {
      setErrorModal(apiErrorMessage(err));
    }
  };

  const deleteEntry = (entry) => {
    setConfirm(null);
    if (editing?.id === entry.id) clearForm();
    setData((cur) => withEntries(cur, cur.entries.filter((en) => en.id !== entry.id)));
    apiFetch(`/timesheets/entries/${entry.id}`, { method: "DELETE" }).catch((err) => {
      setData((cur) => (cur ? withEntries(cur, [...cur.entries, entry]) : cur));
      setErrorModal(apiErrorMessage(err));
    });
  };

  const badge = PERIOD_STATUS[status];
  const monthLabel = `${MONTH_NAMES[view.month - 1]} ${view.year}`;
  const entries = data?.entries || [];
  const totals = data?.totals || { minutes: 0, cost: 0 };
  const groups = useMemo(() => groupEntriesByDescription(entries), [entries]);
  const pendingCount = entries.filter((en) => en.pending).length;

  const lineLabel = (l) =>
    `${l.description}${l.reference_naam ? ` — ${l.reference_naam}` : ""} (${formatEuro(l.rate)}${l.compensation_type === "hourly" ? "/u" : ""})`;

  return (
    <div>
      {errorModal && <ErrorModal message={errorModal} onClose={() => setErrorModal("")} />}
      {confirm?.type === "submit" && (
        <ConfirmModal
          title="Maand indienen"
          message={`Bevestig dat alle prestaties van ${monthLabel} volledig zijn ingegeven. Na het indienen kan je niets meer wijzigen tenzij een beheerder de maand vrijgeeft.`}
          confirmLabel="Indienen"
          busy={saving}
          onConfirm={() => runAction("submit")}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === "delete" && (
        <ConfirmModal
          title="Registratie verwijderen"
          message={`Registratie van ${formatDate(confirm.entry.entry_date)} verwijderen?`}
          confirmLabel="Verwijderen"
          danger
          onConfirm={() => deleteEntry(confirm.entry)}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === "request" && (
        <ConfirmModal
          title="Vrijgave vragen"
          message={
            <div>
              <p className="mb-3">Een beheerder moet de maand vrijgeven voor je ze opnieuw kan aanpassen.</p>
              <label className={labelClass} htmlFor="request-reason">Reden (optioneel)</label>
              <input id="request-reason" type="text" value={releaseReason} onChange={(e) => setReleaseReason(e.target.value)} className={inputClass} />
            </div>
          }
          confirmLabel="Vrijgave vragen"
          busy={saving}
          onConfirm={() => runAction("request-release", { comment: releaseReason || null })}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === "approve" && (
        <ConfirmModal
          title="Maand goedkeuren"
          message={`Prestaties van ${staff.find((m) => m.id === memberId)?.naam || "dit personeelslid"} in ${monthLabel} goedkeuren? De maand wordt daarna vergrendeld.`}
          confirmLabel="Goedkeuren"
          busy={saving}
          onConfirm={() => runAction("approve")}
          onCancel={() => setConfirm(null)}
        />
      )}
      {(confirm?.type === "release" || confirm?.type === "reopen") && (
        <ConfirmModal
          title={confirm.type === "release" ? "Maand terugsturen" : "Maand heropenen"}
          message={
            <div>
              <p className="mb-3">
                {confirm.type === "release"
                  ? "De maand wordt vrijgegeven zodat het personeelslid ze kan aanpassen."
                  : "De goedkeuring vervalt en de maand wordt opnieuw open."}
              </p>
              <label className={labelClass} htmlFor="action-comment">Opmerking (optioneel)</label>
              <input id="action-comment" type="text" value={releaseReason} onChange={(e) => setReleaseReason(e.target.value)} className={inputClass} />
            </div>
          }
          confirmLabel={confirm.type === "release" ? "Terugsturen" : "Heropenen"}
          busy={saving}
          onConfirm={() => runAction(confirm.type, { comment: releaseReason || null })}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* blok: maandnavigatie gecentreerd, status en maandacties rechts */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 mb-4">
        <div />
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => goToMonth(-1)} aria-label="Vorige maand" className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">‹</button>
          <MonthPicker view={view} label={monthLabel} onPick={showMonth} />
          <button onClick={() => goToMonth(1)} aria-label="Volgende maand" className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">›</button>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {loaded && <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badge.className}`}>{badge.label}</span>}
          {status === "open" && entries.length > 0 && (
            <button
              onClick={() => setConfirm({ type: "submit" })}
              disabled={pendingCount > 0}
              title={pendingCount > 0 ? "Even wachten tot alles is opgeslagen" : ""}
              className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              Maand indienen
            </button>
          )}
          {status === "submitted" && memberId === ownId && (
            <button onClick={() => setConfirm({ type: "request" })} className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Vrijgave vragen
            </button>
          )}
          {(status === "submitted" || status === "release_requested") && canRelease && (
            <button onClick={() => setConfirm({ type: "release" })} className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Terugsturen
            </button>
          )}
          {(status === "submitted" || status === "release_requested") && canApprove && (
            <button onClick={() => setConfirm({ type: "approve" })} className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700">
              Goedkeuren
            </button>
          )}
          {status === "approved" && canPdf && (
            <button onClick={downloadPdf} className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              PDF downloaden
            </button>
          )}
          {status === "approved" && canReopen && (
            <button onClick={() => setConfirm({ type: "reopen" })} className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Heropenen
            </button>
          )}
        </div>
      </div>

      {data?.period?.release_reason && status === "open" && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Maand vrijgegeven: {data.period.release_reason}
        </div>
      )}
      {loaded && !editable && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {status === "approved"
            ? "Deze maand is goedgekeurd en kan niet meer gewijzigd worden."
            : "Deze maand is ingediend en wacht op goedkeuring."}
        </div>
      )}

      {!loaded ? null : isInternal ? (
        <InternalMonth memberId={memberId} view={view} status={status} reloadKey={status} />
      ) : (
        <>
          {editable && (
            <form onSubmit={submit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-5">
              <div className="flex flex-wrap items-end gap-4">
                <div className="inline-flex rounded border border-gray-300 overflow-hidden" role="group" aria-label="Soort registratie">
                  {KINDS.map((k) => (
                    <button
                      key={k.key}
                      type="button"
                      onClick={() => update({ kind: k.key })}
                      className={`px-3 py-1.5 text-sm ${form.kind === k.key ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
                <div>
                  <label className={labelClass} htmlFor="entry-date">Datum</label>
                  <input id="entry-date" type="date" value={form.date} onChange={(e) => changeDate(e.target.value)} className={`${inputClass} w-40`} />
                </div>
                {form.kind === "work" && (
                  <div className="w-64">
                    <label className={labelClass} htmlFor="entry-line">Contractlijn</label>
                    <select id="entry-line" value={form.lineId} onChange={(e) => update({ lineId: e.target.value, breakTouched: false })} className={inputClass}>
                      <option value="">{lines.length ? "Kies een lijn..." : "Geen lopende lijn op deze datum"}</option>
                      {lines.map((l) => (
                        <option key={l.id} value={l.id}>{lineLabel(l)}</option>
                      ))}
                    </select>
                  </div>
                )}
                {form.kind === "work" && line && isHourly && (
                  <>
                    <div className="w-28"><TimeInput id="entry-start" label="Start" value={form.start} onChange={(v) => update({ start: v })} /></div>
                    <div className="w-28"><TimeInput id="entry-end" label="Einde" value={form.end} onChange={(v) => update({ end: v })} /></div>
                    <div className="w-24">
                      <label className={labelClass} htmlFor="entry-break">Pauze (min)</label>
                      <input
                        id="entry-break" type="number" min="0" step="5" value={form.breakMinutes}
                        onChange={(e) => update({ breakMinutes: e.target.value, breakTouched: true })}
                        className={inputClass}
                      />
                    </div>
                  </>
                )}
                {form.kind === "work" && line && isPerItem && (
                  <div className="w-28">
                    <label className={labelClass} htmlFor="entry-qty">Aantal</label>
                    <input id="entry-qty" type="number" min="1" step="1" value={form.quantity} onChange={(e) => update({ quantity: e.target.value })} className={inputClass} />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-4 mt-4">
                <div className="min-w-[16rem] flex-1">
                  <label className={labelClass} htmlFor="entry-note">Opmerking (optioneel)</label>
                  <input id="entry-note" type="text" value={form.note} onChange={(e) => update({ note: e.target.value })} className={inputClass} />
                </div>
                <div className="text-sm text-gray-600 pb-2 min-w-[10rem]" aria-live="polite">
                  {minutes ? <strong className="text-gray-800">{formatHoursMinutes(minutes)}</strong> : null}
                  {liveCost !== null && <span>{minutes ? " · " : ""}{formatEuro(liveCost)}</span>}
                </div>
                <div className="flex gap-2">
                  {editing && (
                    <button type="button" onClick={clearForm} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
                      Annuleren
                    </button>
                  )}
                  <button type="submit" className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700">
                    {editing ? "Opslaan" : "Toevoegen"}
                  </button>
                </div>
              </div>
              {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
            </form>
          )}

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 900 }}>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                    <th className="px-4 py-2 text-right">Datum</th>
                    <th className="px-4 py-2 text-left">Omschrijving</th>
                    <th className="px-4 py-2 text-right">Start</th>
                    <th className="px-4 py-2 text-right">Einde</th>
                    <th className="px-4 py-2 text-right">Pauze</th>
                    <th className="px-4 py-2 text-right">Gewerkt</th>
                    <th className="px-4 py-2 text-right">Tarief</th>
                    <th className="px-4 py-2 text-right">Kost</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data && entries.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Nog geen registraties in {monthLabel}.</td></tr>
                  )}
                  {!data && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Laden...</td></tr>
                  )}
                  {groups.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.items.map((en) => {
                        const absence = en.entry_type === "absence";
                        return (
                          <tr key={en.id} className={`border-b border-gray-50 hover:bg-gray-50 ${en.pending ? "opacity-60" : ""}`}>
                            <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                              <span className="text-gray-500 mr-2">{weekdayName(en.entry_date).slice(0, 2).toLowerCase()}</span>{formatDate(en.entry_date)}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {absence ? <span className="text-gray-500">{ABSENCE_LABELS[en.absence_type]}</span> : en.line_description}
                              {en.note && <span className="ml-2 text-xs text-gray-400" title={en.note}>— {en.note}</span>}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">{trimSeconds(en.start_time)}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{trimSeconds(en.end_time)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-600">{en.break_minutes ? formatHoursMinutes(en.break_minutes) : ""}</td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {en.compensation_type === "per_item" ? `${Number(en.quantity)}×` : en.minutes ? formatHoursMinutes(en.minutes) : ""}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                              {en.rate !== null && en.rate !== undefined ? `${formatEuro(en.rate)}${en.compensation_type === "hourly" ? "/u" : ""}` : ""}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-800">{en.cost !== null && en.cost !== undefined ? formatEuro(en.cost) : ""}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              {en.pending ? (
                                <span className="text-xs text-gray-400">opslaan…</span>
                              ) : (
                                editable && (
                                  <>
                                    <button onClick={() => startEdit(en)} className="text-xs text-blue-600 hover:text-blue-800 mr-3">Bewerken</button>
                                    <button onClick={() => setConfirm({ type: "delete", entry: en })} className="text-xs text-red-500 hover:text-red-700">Verwijderen</button>
                                  </>
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {group.kind === "work" && (
                        <tr className="bg-indigo-50 border-b border-indigo-100 text-indigo-900 font-semibold">
                          <td className="px-4 py-1.5" colSpan={5}>Subtotaal {group.label}</td>
                          <td className="px-4 py-1.5 text-right tabular-nums">
                            {[group.minutes ? formatHoursMinutes(group.minutes) : "", group.quantity ? `${group.quantity}×` : ""].filter(Boolean).join(" + ")}
                          </td>
                          <td></td>
                          <td className="px-4 py-1.5 text-right tabular-nums">{group.cost ? formatEuro(group.cost) : ""}</td>
                          <td></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                {entries.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold text-gray-800">
                      <td className="px-4 py-2" colSpan={5}>Totaal {monthLabel}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{totals.minutes ? formatHoursMinutes(totals.minutes) : ""}</td>
                      <td></td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatEuro(totals.cost)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
